import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, ExternalLink, FileText, Image as ImageIcon, FileWarning,
  Loader2, Pencil, Users2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

const emptyForm = {
  fname: "",
  fdesignation: "",
  fdepartment: "",
  fspecialization: "",
  fmob: "",
  femail: "",
  fspecialinterest: "",
  seq: "",
};

// function imageUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/facultyimage/${filename}`;
// }
function imageUrl(filename) {
  if (!filename) return null;
   const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/facultyimage/${fileName}`;
}

// function attachUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/FacultyDocument/${filename}`;
// }
function attachUrl(filename) {
  if (!filename) return null;
   const fileName = filename.split(/[\\/]/).pop();
  return `${STATIC_ORIGIN}/FacultyDocument/${fileName}`;
}

export default function ManageFaculty() {
  const [items, setItems] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterDesignation, setFilterDesignation] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewName, setImagePreviewName] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [attachFile, setAttachFile] = useState(null);
  const [attachPreviewName, setAttachPreviewName] = useState(null);
  const [existingAttach, setExistingAttach] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const imageInputRef = useRef(null);
  const attachInputRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchItems = async (pageNo = page) => {
    setLoading(true);
    setError("");
    try {
      const params = { page: pageNo };
      if (filterDesignation) params.fdesignation = filterDesignation;
      if (filterDepartment) params.fdepartment = filterDepartment;
      const res = await adminApi.get("/admin/faculty", { params });
      setItems(res.data.faculty || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load faculty.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [degRes, deptRes] = await Promise.all([
        adminApi.get("/admin/designation/all"),
        adminApi.get("/admin/department-master"), //new change on 30_7_2026
      ]);
      setDesignations(degRes.data.designations || []);
      setDepartments(deptRes.data.departments || []);
    } catch (err) {
      console.error("Could not load designation/department options:", err);
    }
  };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchItems(page);
    // eslint-disable-next-line
  }, [page]);

  useEffect(() => {
    if (page === 1) {
      fetchItems(1);
    } else {
      setPage(1); // triggers the effect above via the page dependency
    }
    // eslint-disable-next-line
  }, [filterDesignation, filterDepartment]);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    setPage(p);
  };

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === items.length ? [] : items.map((f) => f.faculty_id));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Delete (single or multiple, via checkbox) ───────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected faculty record(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      const res = await adminApi.post("/admin/faculty/bulk-delete", { ids: selected });
      showToast("success", res.data.message || `${selected.length} record(s) deleted.`);
      setSelected([]);
      fetchItems();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected record(s).");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = async () => {
    setEditingId(null);
    setFormError("");
    setImageFile(null);
    setImagePreviewName(null);
    setExistingImage(null);
    setAttachFile(null);
    setAttachPreviewName(null);
    setExistingAttach(null);
    let suggestedSeq = "";
    try {
      const res = await adminApi.get("/admin/faculty/next-sequence");
      suggestedSeq = String(res.data.nextSequence || "");
    } catch {
      // non-critical
    }
    setForm({ ...emptyForm, seq: suggestedSeq });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditingId(item.faculty_id);
    setForm({
      fname: item.fname || "",
      fdesignation: item.fdesignation ? String(item.fdesignation) : "",
      fdepartment: item.fdepartment ? String(item.fdepartment) : "",
      fspecialization: item.fspecialization || "",
      fmob: item.fmob || "",
      femail: item.femail || "",
      fspecialinterest: item.fspecialinterest || "",
      seq: item.seq ? String(item.seq) : "",
    });
    setImageFile(null);
    setImagePreviewName(null);
    setExistingImage(item.fimage);
    setAttachFile(null);
    setAttachPreviewName(null);
    setExistingAttach(item.fattachprofile);
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".jpg", ".jpeg", ".png"].includes(ext)) {
      setFormError("Image must be JPG, JPEG or PNG.");
      return;
    }
    if (f.size > 1 * 1024 * 1024) {
      setFormError("Image size must be less than 1MB.");
      return;
    }
    setFormError("");
    setImageFile(f);
    setImagePreviewName(f.name);
  };

  const handleAttachChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".jpg", ".jpeg", ".png", ".pdf"].includes(ext)) {
      setFormError("Attachment must be JPG, JPEG, PNG or PDF.");
      return;
    }
    setFormError("");
    setAttachFile(f);
    setAttachPreviewName(f.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.fname.trim()) {
      setFormError("Name is required.");
      return;
    }
    if (!form.fdesignation) {
      setFormError("Designation is required.");
      return;
    }
    if (!form.fdepartment) {
      setFormError("Department is required.");
      return;
    }
    if (!form.seq) {
      setFormError("Sequence is required.");
      return;
    }

    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => fd.append(key, value));
    if (imageFile) fd.append("fimage", imageFile);
    if (attachFile) fd.append("fattachprofile", attachFile);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/faculty/${editingId}`, fd);
        showToast("success", "Faculty updated.");
      } else {
        await adminApi.post("/admin/faculty", fd);
        showToast("success", "Faculty added.");
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = items.length > 0 && selected.length === items.length;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Faculty Details
          </h3>
		  
        </div>
		   <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">Designation:</label>
          <select
            value={filterDesignation}
            onChange={(e) => setFilterDesignation(e.target.value)}
            className="min-w-[170px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
          >
            <option value="">-- All --</option>
            {designations.map((d) => (
              <option key={d.deg_id} value={d.deg_id}>
                {d.deg_name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">Department:</label>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="min-w-[170px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
          >
            <option value="">-- All --</option>
            {departments.map((d) => (
              <option key={d.dept_id} value={d.dept_id}>
                {d.dept_name}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 hover:bg-red-100 text-[13px] font-medium px-3 py-1.5 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New Faculty
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 shrink-0 mb-2">
     
      </div>

      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading faculty...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <Users2 size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No faculty yet — click "Add New Faculty" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
              <tr className="text-left text-sm">
                <th className="th-colorful pl-3 pr-2 py-1.5 w-8 border-b-2 border-r normal-case">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                  />
                </th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Name</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Designation</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Department</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Specialization</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Mob No</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r">Email</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r" style={{textAlign:"center"}}>Image</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide border-b-2 border-r" style={{textAlign:"center"}}>Attachment</th>
                <th className="th-colorful px-3 py-1.5 font-bold uppercase tracking-wide w-20 border-b-2">Seq</th>
              </tr>
            </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {items.map((f, i) => (
                    <motion.tr
                      key={f.faculty_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(f)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(f.faculty_id) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="pl-3 pr-2 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.includes(f.faculty_id)}
                          onChange={() => toggleSelectOne(f.faculty_id)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-medium border-r border-slate-200">{f.fname}</td>
                      <td className="px-3 py-2 text-slate-600 border-r border-slate-200">{f.deg_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-600 border-r border-slate-200">{f.dept_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[180px] truncate border-r border-slate-200" title={f.fspecialization}>
                        {f.fspecialization || "—"}
                      </td>
                      <td className="px-3 py-2 text-slate-500 border-r border-slate-200">{f.fmob || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 max-w-[180px] truncate border-r border-slate-200" title={f.femail}>
                        {f.femail || "—"}
                      </td>
                      <td className="px-3 py-2 border-r border-slate-200" style={{textAlign:"center"}}>
                        {f.fimage ? (
                          <a
                            href={imageUrl(f.fimage)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                          >
                               <div style={{border:"0.5px solid #999",padding:"7px",marginTop:"3px",marginBottom:"3px",borderRadius:"7px"}}><i style={{color:"green"}} class="fa fa-eye" aria-hidden="true"></i></div>
                          </a>
                        ) : (
                          <span className="text-slate-300 text-xs">None</span>
                        )}
                      </td>
                      <td className="px-3 py-2 border-r border-slate-200" style={{textAlign:"center"}}>
                          {f.fattachprofile ? (
                          <a
                            href={attachUrl(f.fattachprofile)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                          >
                               <div style={{border:"0.5px solid #999",padding:"7px",marginTop:"3px",marginBottom:"3px",borderRadius:"7px"}}><i style={{color:"green"}} class="fa fa-eye" aria-hidden="true"></i></div>
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-slate-300 text-xs">
                            <FileWarning size={14} /> None
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-500">{f.seq}</td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && totalPages > 1 && (
          <div className="shrink-0 flex items-center justify-between border-t border-slate-200 px-5 py-2">
            <span className="text-xs text-slate-400">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => goToPage(1)}
                disabled={page <= 1}
                title="First page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                title="Previous page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                title="Next page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages}
                title="Last page"
                className="flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Faculty" : "Add New Faculty"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[75vh] overflow-y-auto">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.fname}
                    onChange={(e) => setForm({ ...form, fname: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 col-lg-6">
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                      Designation <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.fdesignation}
                      onChange={(e) => setForm({ ...form, fdesignation: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
                    >
                      <option value="">---Select---</option>
                      {designations.map((d) => (
                        <option key={d.deg_id} value={d.deg_id}>
                          {d.deg_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-6 col-lg-6">
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                      Department <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.fdepartment}
                      onChange={(e) => setForm({ ...form, fdepartment: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
                    >
                      <option value="">---Select---</option>
                      {departments.map((d) => (
                        <option key={d.dept_id} value={d.dept_id}>
                          {d.dept_name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Qualification</label>
                  <input
                    type="text"
                    value={form.fspecialization}
                    onChange={(e) => setForm({ ...form, fspecialization: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                <div className="row">
                  <div className="col-md-6 col-lg-6">
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Mob No</label>
                    <input
                      type="tel"
                      maxLength={10}
                      value={form.fmob}
                      onChange={(e) => setForm({ ...form, fmob: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    />
                  </div>
                  <div className="col-md-6 col-lg-6">
                    <label className="text-xs font-medium text-slate-500 mb-1.5 block">Email</label>
                    <input
                      type="email"
                      value={form.femail}
                      onChange={(e) => setForm({ ...form, femail: e.target.value })}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Reg. No of State Medical Council
                  </label>
                  <textarea
                    value={form.fspecialinterest}
                    onChange={(e) => setForm({ ...form, fspecialinterest: e.target.value })}
                    rows={2}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition resize-none"
                  />
                </div>
<div className="row">
                <div className="col-md-6 col-lg-6">
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Attach Profile — JPG, PNG or PDF, under 1MB (resized to 295×345)
                  </label>
                  <div
                    onClick={() => attachInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <FileText size={18} className="text-slate-300 shrink-0" />
                    <div className="text-xs text-slate-500">
                      {attachPreviewName
                        ? attachPreviewName
                        : existingAttach
                        ? "Click to replace attachment"
                        : "Click to choose a file"}
                    </div>
                    <input
                      ref={attachInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={handleAttachChange}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="col-md-6 col-lg-6">
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Image — JPG, JPEG or PNG, under 1MB (resized to 295×345)
                  </label>
                  <div
                    onClick={() => imageInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <ImageIcon size={18} className="text-slate-300 shrink-0" />
                    <div className="text-xs text-slate-500">
                      {imagePreviewName
                        ? imagePreviewName
                        : existingImage
                        ? "Click to replace image"
                        : "Click to choose an image"}
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>
</div>
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Sequence <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.seq}
                    onChange={(e) => setForm({ ...form, seq: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
